import React, { useState, useEffect, useCallback } from 'react';
import { inviteAdmin, listUsers, listInvitations, setPasswordForCurrentUser, deleteUser, updateUserRoles } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import './AdminManagementPage.css';

function AdminManagementPage() {
  const { user } = useAuth();

  // Invite state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('broker');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);

  // Set password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [showSetPassword, setShowSetPassword] = useState(false);

  // Delete / role state
  const [actionLoading, setActionLoading] = useState(null); // userId being acted on
  const [actionMessage, setActionMessage] = useState(null);

  // Single role per user — hierarchy: admin > broker > user
  // Only show broker+ users in admin panel
  const ASSIGNABLE_ROLES = ['broker', 'admin'];

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await listUsers(0, 100);
      const allUsers = data.users || [];
      // Filter to broker+ only (users whose first role is broker or admin)
      const brokerPlus = allUsers.filter((u) => {
        const role = (u.roles || [])[0];
        return role === 'broker' || role === 'admin';
      });
      setUsers(brokerPlus);
      setUsersTotal(brokerPlus.length);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    setInvitationsLoading(true);
    try {
      const data = await listInvitations();
      setInvitations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    } finally {
      setInvitationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchInvitations();
  }, [fetchUsers, fetchInvitations]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteLoading(true);
    setInviteMessage(null);
    try {
      const res = await inviteAdmin(inviteEmail.trim(), inviteRole);
      setInviteMessage({ type: 'success', text: res.message || `Invitation sent to ${inviteEmail}` });
      setInviteEmail('');
      fetchInvitations(); // refresh
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to send invitation';
      setInviteMessage({ type: 'error', text: detail });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }
    setPasswordLoading(true);
    setPasswordMessage(null);
    try {
      const res = await setPasswordForCurrentUser(newPassword);
      setPasswordMessage({ type: 'success', text: res.message || 'Password set successfully!' });
      setNewPassword('');
      setConfirmPassword('');
      setShowSetPassword(false);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to set password';
      setPasswordMessage({ type: 'error', text: detail });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteUser = async (u) => {
    if (u.email === user?.email) return;
    if (!window.confirm(`Are you sure you want to delete ${u.full_name || u.email}? This cannot be undone.`)) return;
    setActionLoading(u.id);
    setActionMessage(null);
    try {
      const res = await deleteUser(u.id);
      setActionMessage({ type: 'success', text: res.message || 'User deleted' });
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to delete user';
      setActionMessage({ type: 'error', text: detail });
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeRole = async (u, newRole) => {
    const currentRole = (u.roles || [])[0] || 'user';
    if (newRole === currentRole) return; // No change

    const confirmed = window.confirm(
      `Change ${u.full_name || u.email}'s role from "${currentRole}" to "${newRole}"?`
    );
    if (!confirmed) return;

    setActionLoading(u.id);
    setActionMessage(null);
    try {
      await updateUserRoles(u.id, [newRole]);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to update role';
      setActionMessage({ type: 'error', text: detail });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="admin-mgmt">
      {/* ── Invite Section ── */}
      <div className="admin-mgmt-card">
        <h2 className="admin-mgmt-title">✉️ Invite New Member</h2>
        <p className="admin-mgmt-desc">
          Send an email invitation to grant access. Choose the role and the invitation expires in 48 hours.
        </p>
        <form className="admin-mgmt-invite-form" onSubmit={handleInvite}>
          <input
            type="email"
            className="admin-mgmt-input"
            placeholder="user@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <select
            className="admin-mgmt-select"
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            <option value="broker">🏢 Broker</option>
            <option value="admin">🛡️ Admin</option>
          </select>
          <button
            type="submit"
            className="admin-mgmt-btn admin-mgmt-btn-primary"
            disabled={inviteLoading}
          >
            {inviteLoading ? 'Sending…' : 'Send Invitation'}
          </button>
        </form>
        {inviteMessage && (
          <div className={`admin-mgmt-alert admin-mgmt-alert-${inviteMessage.type}`}>
            {inviteMessage.text}
          </div>
        )}
      </div>

      {/* ── Set Password Section ── */}
      <div className="admin-mgmt-card">
        <div className="admin-mgmt-card-header">
          <div>
            <h2 className="admin-mgmt-title">🔑 Set Password</h2>
            <p className="admin-mgmt-desc">
              Set or update your login password. Required if your account was created via Google OAuth.
            </p>
          </div>
          <button
            className="admin-mgmt-btn admin-mgmt-btn-secondary"
            onClick={() => setShowSetPassword(!showSetPassword)}
          >
            {showSetPassword ? 'Cancel' : 'Set Password'}
          </button>
        </div>
        {showSetPassword && (
          <form className="admin-mgmt-password-form" onSubmit={handleSetPassword}>
            <input
              type="password"
              className="admin-mgmt-input"
              placeholder="New password (min 8 chars, 1 uppercase, 1 digit)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
            <input
              type="password"
              className="admin-mgmt-input"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
            <button
              type="submit"
              className="admin-mgmt-btn admin-mgmt-btn-primary"
              disabled={passwordLoading}
            >
              {passwordLoading ? 'Saving…' : 'Save Password'}
            </button>
          </form>
        )}
        {passwordMessage && (
          <div className={`admin-mgmt-alert admin-mgmt-alert-${passwordMessage.type}`}>
            {passwordMessage.text}
          </div>
        )}
      </div>

      {/* ── Users Table ── */}
      <div className="admin-mgmt-card">
        <div className="admin-mgmt-card-header">
          <div>
            <h2 className="admin-mgmt-title">👥 Team Members ({usersTotal})</h2>
            <span className="admin-mgmt-subtitle">Brokers &amp; Admins only</span>
          </div>
          <button
            className="admin-mgmt-btn admin-mgmt-btn-secondary"
            onClick={fetchUsers}
            disabled={usersLoading}
          >
            {usersLoading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        {actionMessage && (
          <div className={`admin-mgmt-alert admin-mgmt-alert-${actionMessage.type}`}>
            {actionMessage.text}
          </div>
        )}
        <div className="admin-mgmt-table-wrap">
          <table className="admin-mgmt-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Provider</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="admin-mgmt-empty">
                    {usersLoading ? 'Loading users…' : 'No brokers or admins found'}
                  </td>
                </tr>
              ) : (
                users.map((u) => {
                  const isYou = u.email === user?.email;
                  const isBusy = actionLoading === u.id;
                  return (
                    <tr key={u.id} className={isYou ? 'admin-mgmt-row-you' : ''}>
                      <td>{u.full_name || '—'} {isYou && <span className="admin-mgmt-you-badge">you</span>}</td>
                      <td>{u.email}</td>
                      <td>
                        {(() => {
                          const currentRole = (u.roles || [])[0] || 'user';
                          return (
                            <select
                              className={`admin-mgmt-role-select admin-mgmt-role-${currentRole}`}
                              value={currentRole}
                              onChange={(e) => handleChangeRole(u, e.target.value)}
                              disabled={isBusy || isYou}
                              title={isYou ? 'Cannot change your own role' : `Current role: ${currentRole}`}
                            >
                              {ASSIGNABLE_ROLES.map((role) => (
                                <option key={role} value={role}>
                                  {role === 'admin' ? '🛡️ Admin' : '🏢 Broker'}
                                </option>
                              ))}
                            </select>
                          );
                        })()}
                      </td>
                      <td className="admin-mgmt-provider">{u.auth_provider || '—'}</td>
                      <td>{formatDate(u.created_at)}</td>
                      <td>
                        {!isYou && (
                          <button
                            className="admin-mgmt-btn admin-mgmt-btn-danger"
                            onClick={() => handleDeleteUser(u)}
                            disabled={isBusy}
                            title="Delete user"
                          >
                            {isBusy ? '…' : '🗑️'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Invitations Table ── */}
      <div className="admin-mgmt-card">
        <div className="admin-mgmt-card-header">
          <h2 className="admin-mgmt-title">📨 Invitations</h2>
          <button
            className="admin-mgmt-btn admin-mgmt-btn-secondary"
            onClick={fetchInvitations}
            disabled={invitationsLoading}
          >
            {invitationsLoading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        <div className="admin-mgmt-table-wrap">
          <table className="admin-mgmt-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Sent</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {invitations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="admin-mgmt-empty">
                    {invitationsLoading ? 'Loading invitations…' : 'No invitations yet'}
                  </td>
                </tr>
              ) : (
                invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td>
                      <span className={`admin-mgmt-role-badge admin-mgmt-role-${inv.role || 'admin'}`}>
                        {(inv.role || 'admin').charAt(0).toUpperCase() + (inv.role || 'admin').slice(1)}
                      </span>
                    </td>
                    <td>
                      {inv.accepted ? (
                        <span className="admin-mgmt-status admin-mgmt-status-accepted">✅ Accepted</span>
                      ) : isExpired(inv.expires_at) ? (
                        <span className="admin-mgmt-status admin-mgmt-status-expired">⏰ Expired</span>
                      ) : (
                        <span className="admin-mgmt-status admin-mgmt-status-pending">⏳ Pending</span>
                      )}
                    </td>
                    <td>{formatDate(inv.created_at)}</td>
                    <td>{formatDate(inv.expires_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminManagementPage;
